/**
 * Dual virtual touch controls for mobile landscape gameplay:
 *   - Left touch area: Movement virtual stick (walk / strafe)
 *   - Right touch area: Aim & Fire virtual stick (aim direction + spell casting)
 *
 * Uses Pointer Events with capture for responsive multi-touch tracking.
 * Floating stick bases reposition dynamically under thumb contact points.
 */

export interface TouchState {
  moveX: number;
  moveY: number;
  aimX: number;
  aimY: number;
  firing: boolean;
  aiming: boolean;
  active: boolean;
}

const STICK_DEADZONE = 0.12;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  parent?: HTMLElement,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  parent?.appendChild(node);
  return node;
}

export class TouchControls {
  private readonly root: HTMLElement;
  private readonly leftZone: HTMLElement;
  private readonly rightZone: HTMLElement;

  private readonly leftBase: HTMLElement;
  private readonly leftKnob: HTMLElement;
  private readonly rightBase: HTMLElement;
  private readonly rightKnob: HTMLElement;
  private readonly dashBtn: HTMLButtonElement;

  private leftPointerId: number | null = null;
  private rightPointerId: number | null = null;

  private leftStartX = 0;
  private leftStartY = 0;
  private rightStartX = 0;
  private rightStartY = 0;

  private moveX = 0;
  private moveY = 0;
  private aimX = 1;
  private aimY = 0;
  private firing = false;
  private aiming = false;
  private dashQueued = false;
  private lastMoveX = 1;
  private lastMoveY = 0;

  private isTouchDevice = false;
  private enabled = true;
  private visible = false;

  constructor(host: HTMLElement = document.body) {
    this.isTouchDevice =
      typeof window !== 'undefined' &&
      ('ontouchstart' in window || navigator.maxTouchPoints > 0 || window.matchMedia('(pointer: coarse)').matches);

    const root = el('div', 'touch-controls', host);
    if (this.isTouchDevice) {
      root.classList.add('touch-device');
    }
    this.root = root;

    // Left Touch Zone (Movement)
    this.leftZone = el('div', 'touch-zone touch-zone-left', root);
    this.leftBase = el('div', 'touch-stick-base touch-left-base', this.leftZone);
    el('div', 'touch-stick-ring', this.leftBase);
    el('span', 'touch-stick-label', this.leftBase, 'MOVE');
    this.leftKnob = el('div', 'touch-stick-knob', this.leftBase);

    // Right Touch Zone (Aim & Fire)
    this.rightZone = el('div', 'touch-zone touch-zone-right', root);
    this.rightBase = el('div', 'touch-stick-base touch-right-base', this.rightZone);
    el('div', 'touch-stick-ring', this.rightBase);
    el('span', 'touch-stick-label', this.rightBase, 'CAST');
    this.rightKnob = el('div', 'touch-stick-knob', this.rightBase);

    this.dashBtn = el('button', 'touch-dash', root, 'DASH');
    this.dashBtn.type = 'button';
    this.dashBtn.setAttribute('aria-label', 'Dash');

    this.bindEvents();
    this.resetLeftVisual();
    this.resetRightVisual();
  }

  public setVisible(show: boolean): void {
    // Never activate the overlay on pure mouse/keyboard desktops — the zones
    // would cover the canvas and intercept clicks even when handlers no-op.
    const next = show && this.isTouchDevice;
    this.visible = next;
    this.root.classList.toggle('visible', next);
    if (!next) {
      this.resetInput();
    }
  }

  public setEnabled(enable: boolean): void {
    this.enabled = enable;
    if (!enable) {
      this.resetInput();
    }
  }

  public getState(): TouchState {
    return {
      moveX: this.moveX,
      moveY: this.moveY,
      aimX: this.aimX,
      aimY: this.aimY,
      firing: this.firing,
      aiming: this.aiming,
      active: this.leftPointerId !== null || this.rightPointerId !== null,
    };
  }

  /** One-shot: true if the dash button was pressed since the last consume. */
  public consumeDash(): boolean {
    if (!this.dashQueued) return false;
    this.dashQueued = false;
    return true;
  }

  public lastMoveDir(): { x: number; y: number } {
    return { x: this.lastMoveX, y: this.lastMoveY };
  }

  /** Knob travel matches the painted ring so short phones do not feel mushy. */
  private stickRadius(base: HTMLElement): number {
    const size = base.offsetWidth || 96;
    return Math.max(26, size * 0.42);
  }

  private resetInput(): void {
    this.releaseLeft();
    this.releaseRight();
    this.dashQueued = false;
    this.dashBtn.classList.remove('active');
  }

  private resetLeftVisual(): void {
    this.leftBase.classList.remove('active');
    this.leftBase.style.removeProperty('--touch-x');
    this.leftBase.style.removeProperty('--touch-y');
    this.leftKnob.style.transform = 'translate3d(0, 0, 0)';
  }

  private resetRightVisual(): void {
    this.rightBase.classList.remove('active');
    this.rightBase.style.removeProperty('--touch-x');
    this.rightBase.style.removeProperty('--touch-y');
    this.rightKnob.style.transform = 'translate3d(0, 0, 0)';
  }

  private hasCapture(el: HTMLElement, pointerId: number): boolean {
    try {
      return el.hasPointerCapture(pointerId);
    } catch {
      return false;
    }
  }

  /** Drop a stick without needing the originating event (overlay / lost capture). */
  private releaseLeft(): void {
    const id = this.leftPointerId;
    this.leftPointerId = null;
    if (id !== null) {
      try {
        this.leftZone.releasePointerCapture(id);
      } catch {
        // already released
      }
    }
    this.moveX = 0;
    this.moveY = 0;
    this.resetLeftVisual();
  }

  private releaseRight(): void {
    const id = this.rightPointerId;
    this.rightPointerId = null;
    if (id !== null) {
      try {
        this.rightZone.releasePointerCapture(id);
      } catch {
        // already released
      }
    }
    this.firing = false;
    this.aiming = false;
    this.resetRightVisual();
  }

  /**
   * Room-clear HUD mutations can swallow pointerup on mobile while leaving the
   * stored id set. The next finger-down then no-ops. Window listeners still see
   * the lift; stale ids without capture get stolen on down.
   */
  private readonly onGlobalPointerEnd = (e: PointerEvent): void => {
    if (e.pointerId === this.leftPointerId) this.releaseLeft();
    if (e.pointerId === this.rightPointerId) this.releaseRight();
  };

  private bindEvents(): void {
    const pointerOpts: AddEventListenerOptions = { passive: false };

    this.leftZone.addEventListener(
      'pointerdown',
      (e: PointerEvent) => {
        if (!this.enabled || !this.visible) return;
        // Mouse aim/fire belongs to the canvas / InputSystem — never consume it here,
        // including hybrid touchscreen laptops where isTouchDevice is true.
        if (e.pointerType === 'mouse') return;
        if (this.leftPointerId === e.pointerId) return;
        if (this.leftPointerId !== null && this.hasCapture(this.leftZone, this.leftPointerId)) return;
        if (this.leftPointerId !== null) this.releaseLeft();
        e.preventDefault();

        this.leftPointerId = e.pointerId;
        try {
          this.leftZone.setPointerCapture(e.pointerId);
        } catch {
          // fallback if capture fails
        }

        const rect = this.leftZone.getBoundingClientRect();
        this.leftStartX = e.clientX;
        this.leftStartY = e.clientY;
        this.leftBase.style.setProperty('--touch-x', `${e.clientX - rect.left}px`);
        this.leftBase.style.setProperty('--touch-y', `${e.clientY - rect.top}px`);
        this.leftBase.classList.add('active');
        this.leftKnob.style.transform = 'translate3d(0, 0, 0)';
        this.moveX = 0;
        this.moveY = 0;
      },
      pointerOpts,
    );

    this.leftZone.addEventListener('pointermove', (e: PointerEvent) => {
      if (e.pointerId !== this.leftPointerId) return;

      const dx = e.clientX - this.leftStartX;
      const dy = e.clientY - this.leftStartY;
      const dist = Math.hypot(dx, dy);

      if (dist > 0.001) {
        const radius = this.stickRadius(this.leftBase);
        const clampDist = Math.min(dist, radius);
        const norm = clampDist / radius;
        const knobX = (dx / dist) * clampDist;
        const knobY = (dy / dist) * clampDist;
        this.leftKnob.style.transform = `translate3d(${knobX.toFixed(1)}px, ${knobY.toFixed(1)}px, 0)`;

        if (norm > STICK_DEADZONE) {
          const scaled = (norm - STICK_DEADZONE) / (1 - STICK_DEADZONE);
          this.moveX = (dx / dist) * scaled;
          this.moveY = (-dy / dist) * scaled;
          this.lastMoveX = this.moveX;
          this.lastMoveY = this.moveY;
        } else {
          this.moveX = 0;
          this.moveY = 0;
        }
      } else {
        this.moveX = 0;
        this.moveY = 0;
        this.leftKnob.style.transform = 'translate3d(0, 0, 0)';
      }
    });

    this.leftZone.addEventListener('lostpointercapture', (e: PointerEvent) => {
      if (e.pointerId === this.leftPointerId) this.releaseLeft();
    });

    this.rightZone.addEventListener(
      'pointerdown',
      (e: PointerEvent) => {
        if (!this.enabled || !this.visible) return;
        if (e.pointerType === 'mouse') return;
        if (this.rightPointerId === e.pointerId) return;
        if (this.rightPointerId !== null && this.hasCapture(this.rightZone, this.rightPointerId)) return;
        if (this.rightPointerId !== null) this.releaseRight();
        e.preventDefault();

        this.rightPointerId = e.pointerId;
        try {
          this.rightZone.setPointerCapture(e.pointerId);
        } catch {
          // fallback if capture fails
        }

        const rect = this.rightZone.getBoundingClientRect();
        this.rightStartX = e.clientX;
        this.rightStartY = e.clientY;
        this.rightBase.style.setProperty('--touch-x', `${e.clientX - rect.left}px`);
        this.rightBase.style.setProperty('--touch-y', `${e.clientY - rect.top}px`);
        this.rightBase.classList.add('active');
        this.firing = true;
        this.aiming = true;
        this.rightKnob.style.transform = 'translate3d(0, 0, 0)';
      },
      pointerOpts,
    );

    this.rightZone.addEventListener('pointermove', (e: PointerEvent) => {
      if (e.pointerId !== this.rightPointerId) return;

      const dx = e.clientX - this.rightStartX;
      const dy = e.clientY - this.rightStartY;
      const dist = Math.hypot(dx, dy);

      this.firing = true;
      this.aiming = true;

      if (dist > 4) {
        const radius = this.stickRadius(this.rightBase);
        const clampDist = Math.min(dist, radius);
        const knobX = (dx / dist) * clampDist;
        const knobY = (dy / dist) * clampDist;
        this.rightKnob.style.transform = `translate3d(${knobX.toFixed(1)}px, ${knobY.toFixed(1)}px, 0)`;
        this.aimX = dx / dist;
        this.aimY = -dy / dist;
      } else {
        this.rightKnob.style.transform = 'translate3d(0, 0, 0)';
      }
    });

    this.rightZone.addEventListener('lostpointercapture', (e: PointerEvent) => {
      if (e.pointerId === this.rightPointerId) this.releaseRight();
    });

    this.dashBtn.addEventListener(
      'pointerdown',
      (e: PointerEvent) => {
        if (!this.enabled || !this.visible) return;
        if (e.pointerType === 'mouse') return;
        e.preventDefault();
        e.stopPropagation();
        this.dashQueued = true;
        this.dashBtn.classList.add('active');
      },
      pointerOpts,
    );
    const releaseDash = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      e.stopPropagation();
      this.dashBtn.classList.remove('active');
    };
    this.dashBtn.addEventListener('pointerup', releaseDash);
    this.dashBtn.addEventListener('pointercancel', releaseDash);

    window.addEventListener('pointerup', this.onGlobalPointerEnd, true);
    window.addEventListener('pointercancel', this.onGlobalPointerEnd, true);
  }

  public destroy(): void {
    window.removeEventListener('pointerup', this.onGlobalPointerEnd, true);
    window.removeEventListener('pointercancel', this.onGlobalPointerEnd, true);
    this.root.remove();
  }
}
